import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { trackError, trackTaquinMoves } from "../../../../lib/metrics";
import { useInspections } from "../../../../hooks/useInspections";
import { useHints } from "../../../../hooks/useHints";
import { ModalRegister, Modal } from "../../../../components/ui/ModalRegister";
import { RhRegisterTaquin, TAQUIN_LABELS_6J } from "../../../origami/components/puzzles/rhRegisterTaquin";
import { DrawerPuzzle } from "../../../origami/components/puzzles/DrawerPuzzle";
import { BOX_PASSWORD } from "../../../../data/passwords";
import { DICTIONARY_PAGES, BOOK1_PAGES, BOOK2_PAGES } from "../../../../data/origami/book_pages";
import RaisedHandsBadge from "../../../../components/ui/RaisedHandsBadge";
import NotebookViewer from "../../../origami/components/puzzles/NotebookViewer";
import { useInspectProgress } from "../../../../hooks/useInspectProgress";
import InspectProgressChip from "../../../../components/ui/InspectProgressChip";
import { useMissionGuide } from "../../../../hooks/useMissionGuide";
import { getInspectKeys } from "../../../../data/inspectables";
import { NeonBrokenCodePad } from "../../../origami/components/puzzles/NeonBrokenCodePad";
import CableSchemasPanel from "../../../origami/components/puzzles/CableSchemasPanel";
import { MORSE_TABLE_PRETTY } from "../../../../data/origami/morseCode";
import HintsModalHub from "../../../../components/ui/HintsModalHub";
import OrigamiCommunicationModal from "../../../origami/components/ui/OrigamiCommunicationModal";
import { useOrigamiReboot } from "../../../../hooks/useOrigamiReboot";
import { rebootText } from "../../../../data/origami/origamiTexts";

const API = process.env.REACT_APP_API_URL;

export default function Stage1Player4Room({
  code,
  playerId,
  hintSignal,
  onInventoryUpdate,
  raisedCount,
  raisedPlayers,
  centerMaxH,
  pseudo,
  playerCount,
  hasDrawerRapatriated,
}) {
  const INSPECT_KEYS = useMemo(() => {
    return getInspectKeys({ gameId: 1, playerCount, stage: 1, seat: 4 });
  }, [playerCount]);

  const taquinSentRef = useRef(false);
  const morseSharedSentRef = useRef(false);
  const MORSE_MARKER = "[DICO_MORSE_V1]";

  // — Modales — (book3/4/5 et shelf supprimés)
  const [showBoxModal, setShowBoxModal] = useState(false);
  const [showDictionaryModal, setShowDictionaryModal] = useState(false);
  const [showBook1Modal, setShowBook1Modal] = useState(false);
  const [showBook2Modal, setShowBook2Modal] = useState(false);
  const [showHintModal, setShowHintModal] = useState(false);
  const [showRhNoteModal, setShowRhNoteModal] = useState(false);
  const [showNeonPadModal, setShowNeonPadModal] = useState(false);
  const [showDrawerModal, setShowDrawerModal] = useState(false);
  const [showDrawerNoteModal, setShowDrawerNoteModal] = useState(false);

  // — États —
  const [isDictionaryUnlocked, setIsDictionaryUnlocked] = useState(false);
  const [boxPassword, setBoxPassword] = useState("");
  const [boxError, setBoxError] = useState("");
  const [hasBoxKey, setHasBoxKey] = useState(false);
  const [dictionaryPage, setDictionaryPage] = useState(0);
  const [hasOpenedDictionary, setHasOpenedDictionary] = useState(false); // eslint-disable-line no-unused-vars
  const [hasSeenMorsePage, setHasSeenMorsePage] = useState(false); // eslint-disable-line no-unused-vars
  const [rhPuzzleSolved, setRhPuzzleSolved] = useState(false);
  const [rhTaquinMoves, setRhTaquinMoves] = useState(0);
  const [drawerUnlocked, setDrawerUnlocked] = useState(false);
  const [neonPadSolved, setNeonPadSolved] = useState(false);
  const [bgReady, setBgReady] = useState(false);
  const [bgShown, setBgShown] = useState(false);

  // — Hooks —
  const { inspectedCount, total } = useInspectProgress({
    sessionCode: code, stage: 1, playerId: Number(playerId), keys: INSPECT_KEYS,
  });
  const { showOrigamiReboot, setShowOrigamiReboot } = useOrigamiReboot({ code, playerId });
  const { markInspectedOnce } = useInspections({ sessionCode: code, stage: 1, playerId: Number(playerId) });
  const { markHintUsedOnce } = useHints({ sessionCode: code, stage: 1, playerId: Number(playerId) });
  const { missionUnlock, missionResolve } = useMissionGuide({ code, playerId, stage: 1 });

  const usedStorageKey = useMemo(() => `hints_used:${code}:s1:p${playerId}`, [code, playerId]);

  // — Assets —
  const backgroundUrl = "/assets/s1-p4-room.webp";
  const boxImg = "/assets/p4_box_glass.webp";
  const dictionaryImg = "/assets/p4_big_book.webp";
  const book1Img = "/assets/p4_book1.webp";
  const book2Img = "/assets/p4_book2.webp";
  const rhNoteImg = "/assets/p4_postit-rh.webp";
  const neonPadImg = "/assets/p4_neonPad.webp";
  const drawerNoteImg = "/assets/4p-s1-p4-drawer-note.webp";
  const deskImg = "/assets/4p-s1-p4-desk.webp";

  useEffect(() => {
    let cancelled = false;
    setBgReady(false); setBgShown(false);
    const img = new Image(); img.src = backgroundUrl;
    (async () => {
      try {
        if (img.decode) await img.decode();
        else await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
        if (!cancelled) { setBgReady(true); requestAnimationFrame(() => setBgShown(true)); }
      } catch { if (!cancelled) { setBgReady(true); setBgShown(true); } }
    })();
    return () => { cancelled = true; };
  }, [backgroundUrl]);

  useEffect(() => { if (!hintSignal) return; setShowHintModal(true); }, [hintSignal]);

  // — Morse shared notes —
  function buildMorseSharedBlock() {
    return `\n\n${MORSE_MARKER}\n📡 Dictionnaire Morse (trouvé par ${pseudo || "J4"})\n\n${MORSE_TABLE_PRETTY}\n\n`;
  }

  async function appendMorseToSharedOnce() {
    if (morseSharedSentRef.current) return;
    morseSharedSentRef.current = true;
    try {
      const res = await axios.get(`${API}/api/sessions/${code}/shared-notes`);
      const current = res.data?.text ?? "";
      if (current.includes(MORSE_MARKER)) return;
      await axios.patch(`${API}/api/sessions/${code}/shared-notes`, {
        playerId, text: current + buildMorseSharedBlock(),
      });
    } catch { morseSharedSentRef.current = false; }
  }

  // — Effet page dictionnaire → morse —
  const currentDictionnaryPage = DICTIONARY_PAGES[dictionaryPage];
  useEffect(() => {
    if (currentDictionnaryPage?.type === "morse") {
      setHasSeenMorsePage(true);
      missionUnlock({ key: "s1.p4.dictionary.morseSeen", text: `${pseudo} a trouvé une table de morse dans le dictionnaire.` });
      missionResolve({ key: "s1.p4.dictionary.unlocked" });
      appendMorseToSharedOnce();
    }
  }, [dictionaryPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // — Taquin —
  function taquinHintForMoves(moves) {
    if (moves >= 100) return { tone: "hard", title: "Indice interne — technique utile", text: "N'hésite pas à consulter un indice ou la solution en cas de blocage." };
    if (moves >= 50) return { tone: "hard", title: "Indice interne — technique utile", text: "Si une tuile est 'bonne' mais bloque la fin d'une ligne, pense à faire tourner la case vide en boucle pour permuter 2 tuiles sans casser tout le reste." };
    if (moves >= 25) return { tone: "soft", title: "Indice interne — méthode", text: "Fais-le par zones : première ligne complète, puis deuxième ligne. Ensuite, termine les 2 dernières lignes ensemble (la position de la case vide est la clé)." };
    return null;
  }
  const taquinHint = taquinHintForMoves(rhTaquinMoves);

  function sendTaquinMovesOnce(moves) {
    if (taquinSentRef.current) return;
    taquinSentRef.current = true;
    trackTaquinMoves({ sessionCode: code, stage: 1, playerId, moves: Math.max(0, Number(moves) || 0) });
  }

  // — Handlers —
  const handleBoxSubmit = (e) => {
    e.preventDefault();
    if (boxPassword.trim().toUpperCase() === BOX_PASSWORD) {
      setHasBoxKey(true); setBoxError("");
      missionResolve({ key: "s1.p4.glassBox.found" });
      missionResolve({ key: "s1.p2.samir.opened" });
      missionUnlock({ key: "s1.p4.glassBox.opened", text: `${pseudo} a déverrouillé la boîte en verre: la clé du dictionnaire est récupérée.` });
      if (typeof onInventoryUpdate === "function") onInventoryUpdate({ hasKeyJ4: true });
    } else {
      trackError({ sessionCode: code, stage: 1, playerId, reason: "wrong_box_password" });
      setBoxError("Ce n'est pas le bon mot de passe.");
    }
  };

  const handleOpenDrawer = () => {
    setShowDrawerModal(true);
    missionUnlock({ key: "s1.p4.drawer.found", text: `${pseudo} a trouvé un tiroir de bureau verrouillé.` });
  };
  const handleDrawerSolved = () => {
    setDrawerUnlocked(true);
    missionUnlock({ key: "s1.p4.drawer.opened", text: `${pseudo} a déverrouillé le tiroir et trouve les schémas de branchements.` });
    missionResolve({ key: "s1.p4.drawer.found" });
  };

  // — Hints —
  const hintCategories = useMemo(() => {
    const cats = [
      {
        id: "box", title: "Boîte en verre", subtitle: "Récupérer la clé",
        hintKeyPrefix: "s1:p4:box",
        levels: {
          l1: (<><p className="mb-2">Le petit pictogramme de chien à côté du pavé n'est pas décoratif.</p></>),
          l2: (<><p className="mb-2">Le code n'est pas dans cette pièce.</p><p className="text-xs text-gray-300">Il faut retrouver le nom du chien de Samir (ça traîne dans les infos RH / carnets).</p></>),
          l3: (<><p className="mb-2">Samir a bien un chien.</p><p className="text-xs text-gray-300">Si vous avez accès au registre des employés ou aux carnets, cherchez le nom exact.</p></>),
          answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (boîte en verre)</p><p className="mt-2 text-xs text-gray-200">Mot de passe : <span className="font-bold text-amber-200">Balthazar</span></p></>),
        },
      },
      {
        id: "dictionary", title: "Dictionnaire / Morse", subtitle: "Ouvrir + trouver la page utile",
        hintKeyPrefix: "s1:p4:dictionary",
        levels: {
          l1: (<><p className="mb-2">Le dictionnaire est verrouillé par un petit cadenas.</p><p className="text-xs text-gray-300">Il faut d'abord récupérer la clé (elle est dans la boîte en verre).</p></>),
          l2: (<><p className="mb-2">Une fois ouvert, feuillette : il y a une page qui sert directement à un autre joueur.</p></>),
          l3: (<><p className="mb-2">Cherche la table de morse.</p><p className="text-xs text-gray-300">Dès que tu la vois, partage-la : elle permet de décoder un signal lumineux dans une autre salle.</p></>),
          answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (quoi faire)</p><p className="mt-2 text-xs text-gray-200">Déverrouille le dictionnaire avec la clé, trouve la page "morse" et partage la table au joueur qui voit le signal clignotant.</p></>),
        },
      },
    ];

    if (hasDrawerRapatriated) {
      cats.push({
        id: "drawer", title: "Bureau à déverrouiller", subtitle: "Ouvrir le tiroir (classeur racks)",
        hintKeyPrefix: "s1:p4:drawer",
        levels: {
          l1: (<><p className="mb-2">La serrure du tiroir est à 3 chiffres… mais elle te donne aussi une logique.</p><p className="text-xs text-gray-300">Les pictogrammes (▲ ◯ ▭) correspondent à un schéma qu'on retrouve sur la note griffonnée.</p></>),
          l2: (<><p className="mb-2">Regarde bien l'ordre des trois formes sur la feuille.</p><p className="text-xs text-gray-300">C'est littéralement le "mode d'emploi" du code.</p></>),
          l3: (<><p>L'ordre des formes et leur nombre sont importants.</p></>),
          answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (tiroir)</p><p className="mt-2 text-xs text-gray-200">Code : <span className="font-bold text-amber-200">623</span></p></>),
        },
      });
    }

    cats.push(
      {
        id: "taquin", title: "Taquin", subtitle: "Reconstituer la note RH",
        hintKeyPrefix: "s1:p4:taquin",
        levels: {
          l1: (<><p className="mb-2">Ce n'est pas "au hasard" : la note doit redevenir lisible, ligne par ligne.</p></>),
          l2: (<><p className="mb-2">Méthode : complète la première ligne, puis la deuxième.</p><p className="text-xs text-gray-300">Ensuite, termine les deux dernières lignes ensemble (la case vide sert à faire "tourner" les pièces).</p></>),
          l3: (<><p className="mb-2">Si tu as l'impression d'être bloqué "à deux pièces près", c'est normal.</p><p className="text-xs text-gray-300">Fais un petit cycle avec la case vide pour permuter sans casser tout le reste.</p></>),
          answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (message complet)</p><p className="mt-2 text-xs text-gray-200"><span className="font-bold text-amber-200">Le registre des employés et de leurs ID ne doit plus trainer sur la table en salle de réunion. Ne laisser aucune copie ailleurs.</span></p></>),
        },
      },
      {
        id: "neonpad", title: "Pavé numérique", subtitle: "Afficheur cassé (segments manquants)",
        hintKeyPrefix: "s1:p4:neonpad",
        levels: {
          l1: (<><p className="mb-2">Ce n'est pas 9887.</p><p className="text-xs text-gray-300">L'écran est "cassé"</p></>),
          l2: (<><p className="mb-2">Le code attendu n'est pas un chiffre "lisible" directement.</p></>),
          l3: (<><p>Les éléments manquants sont importants, fais abstraction de ce que tu vois.</p></>),
          answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (pavé)</p><p className="mt-2 text-xs text-gray-200">Il faut compter les segments "éteints" sur chaque chiffre. Le code est : <span className="font-bold text-amber-200">2132</span></p></>),
        },
      },
    );

    return cats;
  }, [hasDrawerRapatriated]);

  // — Dictionnaire reader —
  const DictionaryReader = () => (
    <div className="flex flex-col items-center">
      <div className="w-80 min-h-[420px] p-6 rounded-xl shadow-xl bg-amber-50 text-black">
        {currentDictionnaryPage?.type === "cover" ? (
          <div className="flex flex-col h-full justify-center items-center">
            <h2 className="text-2xl font-bold text-center">{currentDictionnaryPage.title}</h2>
            <p className="mt-3 text-xs text-gray-600 text-center">Un lourd dictionnaire technique rempli de systèmes de notation et de codes.</p>
          </div>
        ) : (
          <div>
            <h3 className="text-sm font-semibold mb-2">{currentDictionnaryPage?.title}</h3>
            <p className="whitespace-pre-wrap text-[13px] leading-relaxed font-mono">{currentDictionnaryPage?.body}</p>
          </div>
        )}
      </div>
      <div className="flex gap-6 mt-4 text-white items-center">
        <button onClick={() => setDictionaryPage((p) => Math.max(0, p - 1))} disabled={dictionaryPage === 0}
          className="px-2 py-1 rounded-md bg-black/40 border border-white/15 disabled:opacity-40">◀</button>
        <span className="text-xs text-gray-300">Page {dictionaryPage + 1} / {DICTIONARY_PAGES.length}</span>
        <button onClick={() => setDictionaryPage((p) => Math.min(DICTIONARY_PAGES.length - 1, p + 1))} disabled={dictionaryPage === DICTIONARY_PAGES.length - 1}
          className="px-2 py-1 rounded-md bg-black/40 border border-white/15 disabled:opacity-40">▶</button>
      </div>
    </div>
  );

  return (
    <div className="relative w-full bg-black text-white flex items-center justify-center">
      <div className="relative h-screen w-auto mx-auto aspect-square"
        style={{ maxHeight: centerMaxH ? `${centerMaxH}px` : undefined }}>

        <div className="absolute right-4 top-4 z-40 flex items-center gap-2 w-fit">
          <InspectProgressChip inspectedCount={inspectedCount} total={total} />
          <RaisedHandsBadge count={raisedCount} players={raisedPlayers} />
        </div>
        <div className="absolute left-4 top-4 rounded-full bg-black/70 border border-white/15 px-3 py-1 text-[11px] tracking-[0.18em] uppercase text-slate-200 z-10">
          Bureau de Samir
        </div>

        <img src={backgroundUrl} alt="Bureau avec bibliothèques et dictionnaires"
          className={["absolute inset-0 h-full w-full object-contain transition-opacity duration-200", bgShown ? "opacity-100" : "opacity-0"].join(" ")}
          decoding="async" loading="eager"
        />

        {bgReady && (
          <div className="absolute inset-0">
            {/* Pad néon */}
            <img src={neonPadImg} alt="Un pavé numérique"
              onClick={() => { markInspectedOnce('stage1player4:neonPad'); missionUnlock({ key: "s1.p4.neonPad.found", text: `Un pavé numérique étrange est trouvé par ${pseudo}.` }); setShowNeonPadModal(true); }}
              className="p4-neonPad absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
            />

            {/* Tiroir rapatrié — 4J uniquement */}
            {hasDrawerRapatriated && (
              <>
                <img src={deskImg} alt="Tiroir verrouillé"
                  onClick={() => { markInspectedOnce('stage1player4:desk'); handleOpenDrawer(); }}
                  className="fourPlayers-s1-p4-desk absolute cursor-pointer transition-all duration-200 hover:brightness-125 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
                />
                <img src={drawerNoteImg} alt="Notes"
                  onClick={() => { markInspectedOnce('stage1player4:drawerNote'); setShowDrawerNoteModal(true); }}
                  className="fourPlayers-s1-p4-drawer-note absolute cursor-pointer transition-all duration-200 hover:brightness-125 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
                />
              </>
            )}

            {/* Boîte en verre */}
            <img src={boxImg} alt="Boîte en verre verrouillée"
              onClick={() => { markInspectedOnce("stage1player4:glassBox"); setShowBoxModal(true); missionUnlock({ key: "s1.p4.glassBox.found", text: `Une boîte en verre verrouillée contenant une clé est trouvée par ${pseudo}.` }); }}
              className="p4-box absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
            />

            {/* Dictionnaire */}
            <img src={dictionaryImg} alt="Gros dictionnaire de systèmes et signaux"
              onClick={() => { markInspectedOnce('stage1player4:dictionary'); setShowDictionaryModal(true); setHasOpenedDictionary(true); missionUnlock({ key: "s1.p4.dictionary.found", text: `${pseudo} a trouvé un dictionnaire des signaux. Mais il est verrouillé.` }); }}
              className="p4-dictionary absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
            />

            {/* Livres 1 et 2 uniquement (3/4/5 supprimés) */}
            <img src={book1Img} alt="Un livre"
              onClick={() => { markInspectedOnce('stage1player4:book1'); setShowBook1Modal(true); }}
              className="p4-book1 absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
            />
            <img src={book2Img} alt="Un livre"
              onClick={() => { markInspectedOnce('stage1player4:book2'); setShowBook2Modal(true); }}
              className="p4-book2 absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
            />

            {/* Note RH */}
            <img src={rhNoteImg} alt="Note sur les registres RH"
              onClick={() => { markInspectedOnce('stage1player4:rhNote'); setShowRhNoteModal(true); }}
              className="p4-note-rh absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
            />
          </div>
        )}
      </div>

      {/* === MODALES === */}

      {/* Boîte en verre */}
      <Modal open={showBoxModal} title="Boîte verrouillée" onClose={() => setShowBoxModal(false)} onHint={() => setShowHintModal(true)}>
        <p className="mb-2">Une petite boîte en verre parfaitement transparente. À l'intérieur, une clé métallique. Sur le devant, un petit pavé numérique avec un pictogramme de chien…</p>
        {!hasBoxKey ? (
          <form onSubmit={handleBoxSubmit} className="space-y-3 mt-3">
            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">Mot de passe</label>
              <div className="flex items-center gap-2">
                <span className="text-xl" aria-hidden="true">🐶</span>
                <input type="text" value={boxPassword}
                  onChange={(e) => { setBoxPassword(e.target.value); setBoxError(""); }}
                  className="flex-1 px-3 py-2 rounded-lg bg-black/60 border border-white/20 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder=""
                />
              </div>
            </div>
            {boxError && <p className="text-xs text-red-400">{boxError}</p>}
            <button type="submit" className="w-full py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-sm font-semibold">Valider</button>
          </form>
        ) : (
          <p className="mt-3 text-sm text-emerald-300">
            Le pavé émet un petit bip, le loquet se libère. Tu récupères la clé.
            <span className="block text-xs text-emerald-200 mt-1">Elle semble faite pour un petit cadenas de livre.</span>
          </p>
        )}
      </Modal>

      {/* Dictionnaire */}
      <ModalRegister open={showDictionaryModal} title="Dictionnaire des systèmes & signaux" onClose={() => setShowDictionaryModal(false)} onHint={() => setShowHintModal(true)}>
        {!isDictionaryUnlocked ? (
          <>
            <p className="mb-2">Un énorme volume maintenu fermé par un petit cadenas métallique.</p>
            {!hasBoxKey && <p className="text-xs text-gray-300 mb-3">Sans clé, impossible de l'ouvrir. Tu as la nette impression que ce dictionnaire va devenir important pour quelqu'un.</p>}
            <button
              onClick={() => {
                if (hasBoxKey) {
                  setIsDictionaryUnlocked(true);
                  missionUnlock({ key: "s1.p4.dictionary.unlocked", text: `${pseudo} a déverrouillé le dictionnaire des signaux.` });
                  missionResolve({ key: "s1.p4.glassBox.opened" });
                  missionResolve({ key: "s1.p4.dictionary.found" });
                }
              }}
              className="mt-1 w-full py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-sm font-semibold disabled:opacity-40"
              disabled={!hasBoxKey}
            >
              {hasBoxKey ? "Utiliser la clé pour déverrouiller" : "Il te manque encore la clé…"}
            </button>
          </>
        ) : <DictionaryReader />}
      </ModalRegister>

      {/* Tiroir rapatrié — 4J uniquement */}
      {hasDrawerRapatriated && (
        <>
          <ModalRegister open={showDrawerModal} title="Bureau massif" onClose={() => setShowDrawerModal(false)} onHint={() => setShowHintModal(true)}>
            {!drawerUnlocked && (
              <>
                <p>Un tiroir lourd, étiquette : <span className="font-mono text-xs bg-black/60 px-2 py-1 rounded">DOCUMENTATION RACKS / NE PAS ÉGARER</span></p>
                <p className="text-sm text-gray-300">Il est verrouillé.</p>
              </>
            )}
            {drawerUnlocked && <p>Le tiroir est désormais <span className="text-green-300 font-semibold">ouvert</span>.</p>}
            <DrawerPuzzle solved={drawerUnlocked} onSolved={handleDrawerSolved} sessionCode={code} playerId={playerId} unlocked={drawerUnlocked} />
            {drawerUnlocked && (
              <div className="mt-4 rounded-xl border border-emerald-400/25 bg-emerald-500/10 p-3">
                <p className="text-sm text-emerald-100 font-semibold">✅ Classeur ouvert : schéma de câblage (actuel)</p>
                <div className="mt-3 rounded-xl border border-white/10 bg-black/40 p-3">
                  <CableSchemasPanel />
                  <p className="mt-2 text-[11px] text-slate-400 italic text-center">Schéma de câblage officiel — racks / serveurs</p>
                </div>
              </div>
            )}
          </ModalRegister>

          <ModalRegister open={showDrawerNoteModal} title="Notes griffonnées" onClose={() => setShowDrawerNoteModal(false)} onHint={() => setShowHintModal(true)}>
            <p className="text-sm">Des notes difficiles à lire :</p>
            <p className="mt-2 italic text-sm text-gray-200">"Penser à ranger les schémas DANS LE TIROIR cette fois.<br />Et arrêter de tout rebrancher au hasard, MERCI."<br /><span className="text-xs text-right block">Jean-Pierre</span></p>
            <div className="rounded-xl border border-white/10 bg-black/40 p-3 flex justify-center">
              <img src="/assets/p5_note_schema.webp" alt="Schéma griffonné" className="w-full max-w-sm h-auto object-contain" />
            </div>
          </ModalRegister>
        </>
      )}

      {/* Pavé néon */}
      <ModalRegister open={showNeonPadModal} title="Pavé numérique" onClose={() => setShowNeonPadModal(false)} onHint={() => setShowHintModal(true)}>
        <NeonBrokenCodePad
          solved={neonPadSolved}
          onSolved={() => { setNeonPadSolved(true); missionResolve({ key: "s1.p4.neonPad.found" }); }}
          sessionCode={code}
          stage={1}
          playerId={playerId}
        />
      </ModalRegister>

      {/* Livres 1 et 2 */}
      <Modal open={showBook1Modal} title="46 nuances de Rose" onClose={() => setShowBook1Modal(false)}>
        <NotebookViewer pages={BOOK1_PAGES} hintStorageKey={`hint:pageflip:${code}:book1`} />
      </Modal>
      <Modal open={showBook2Modal} title="Le Manuel Secret des Objets Suspects" onClose={() => setShowBook2Modal(false)}>
        <NotebookViewer pages={BOOK2_PAGES} hintStorageKey={`hint:pageflip:${code}:book2`} />
      </Modal>

      {/* Note RH / Taquin */}
      <ModalRegister open={showRhNoteModal} title="Note de Samir" onClose={() => setShowRhNoteModal(false)} onHint={() => setShowHintModal(true)}>
        {!hasDrawerRapatriated ? (
          <>
            <p className="mb-2">Une feuille de notes traine sur le sol. On dirait qu'elle a été découpée en morceaux. Un petit bout est encore lisible :</p>
            <p className="text-xs text-gray-200 whitespace-pre-wrap mb-3">{`« Rappel : le registre des employés et de leurs ID NE DOIT PAS rester dans cette salle.\n\nÀ conserver uniquement dans l'endroit adapté : »`}</p>
          </>
        ) : (
          <p className="mb-2">Une feuille de notes traine sur le sol. On dirait qu'elle a été découpée en morceaux.</p>
        )}
        <RhRegisterTaquin
          solvedStatic={rhPuzzleSolved}
          initialMoves={rhTaquinMoves}
          onMovesChange={(m) => setRhTaquinMoves(m)}
          onSolved={(moves) => { setRhPuzzleSolved(true); setRhTaquinMoves(moves); sendTaquinMovesOnce(moves); }}
          labels={!hasDrawerRapatriated && playerCount === 6 ? TAQUIN_LABELS_6J : undefined}
        />
        {!rhPuzzleSolved && taquinHint && (
          <div className={["mt-3 rounded-xl border p-3 text-xs", taquinHint.tone === "hard" ? "border-amber-400/30 bg-amber-500/10 text-amber-100" : "border-white/10 bg-black/30 text-slate-200"].join(" ")}>
            <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mb-1">{taquinHint.title}</p>
            <p className="leading-relaxed">{taquinHint.text}</p>
            <p className="mt-2 text-[11px] text-slate-400">Coups : <span className="font-semibold text-amber-200">{rhTaquinMoves}</span></p>
          </div>
        )}
        {rhPuzzleSolved && (
          <p className="mt-3 text-[11px] text-emerald-200">
            La phrase complète devient lisible : {hasDrawerRapatriated
              ? <>le registre des employés <span className="font-semibold">et de leurs ID ne doit plus trainer sur la table. En salle de réunion. Ne laisser aucune copie ailleurs</span>.</>
              : <>le registre est bien <span className="font-semibold">dans le bureau RH, sous clé. Ne laisser aucune copie ailleurs.</span></>
            }
          </p>
        )}
      </ModalRegister>

      {/* Reboot Origami */}
      <OrigamiCommunicationModal open={showOrigamiReboot} text={rebootText}
        onClose={() => setShowOrigamiReboot(false)} autoClose={true} autoCloseDuration={10000}
      />

      {/* Indices */}
      <HintsModalHub open={showHintModal} onClose={() => setShowHintModal(false)}
        title="Indices" markHintUsedOnce={markHintUsedOnce}
        categories={hintCategories} usedStorageKey={usedStorageKey}
      />
    </div>
  );
}